import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, call, callVoid } from "../../shared/api/client";
import { queryKeys } from "../../shared/api/queries";
import type { components } from "../../shared/api/schema.gen";

export type Notification = components["schemas"]["Notification"];
export type NotificationPreferences = components["schemas"]["NotificationPreferences"];

/** SA-04 §6: mention notifications are `comment.mention` rows of the caller's own notification list. */
export const MENTION_EVENT_TYPE = "comment.mention";

export const mentionKeys = {
  unread: ["me", "notifications", MENTION_EVENT_TYPE, "unread"] as const,
  preferences: ["me", "notification-preferences"] as const,
};

export function useUnreadMentions() {
  return useQuery({
    queryKey: mentionKeys.unread,
    queryFn: () => call(apiClient.GET("/me/notifications", { params: { query: { unreadOnly: true, eventType: MENTION_EVENT_TYPE, limit: 100 } } })),
  });
}

/** Marks one mention read; the Needs attention count and the list refresh together. */
export function useMarkMentionRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: number) => call(apiClient.POST("/me/notifications/{notificationId}:read", { params: { path: { notificationId } } })),
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: mentionKeys.unread }), queryClient.invalidateQueries({ queryKey: queryKeys.needsAttention })]);
    },
  });
}

export function useMarkAllMentionsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => callVoid(apiClient.POST("/me/notifications:read-all", { params: { query: { eventType: MENTION_EVENT_TYPE } } })),
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: mentionKeys.unread }), queryClient.invalidateQueries({ queryKey: queryKeys.needsAttention })]);
    },
  });
}

/** SA-04 §7.11: the caller's own row; `enabled` false skips the request (e.g. the account menu until it opens). */
export function useNotificationPreferences(enabled = true) {
  return useQuery({
    queryKey: mentionKeys.preferences,
    enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: () => call(apiClient.GET("/me/notification-preferences")),
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mentionEmail: boolean) => call(apiClient.PUT("/me/notification-preferences", { body: { mentionEmail } })),
    onSuccess: (data) => {
      queryClient.setQueryData(mentionKeys.preferences, data);
    },
  });
}
